import React, { useContext, useState, useEffect, Suspense, lazy } from 'react';
import { useForm } from 'react-hook-form';
import { Image, Row, Col } from 'react-bootstrap';
import { Label } from 'reactstrap';
import Popup from 'reactjs-popup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-regular-svg-icons';
import { faArrowUpFromBracket, faUserXmark } from '@fortawesome/free-solid-svg-icons';
import { SpinnerDotted } from 'spinners-react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import { TableContext } from '../../contexts/tableContext';
import { Entry } from '../../redux/entrySlice';
import envValues from '../../enviornment';
import { setCookies } from '../../hooks/useCookies';
import styles from './settings.module.scss';

const EmailInput = lazy(() => import('../../components/EmailBox/EmailBox'));

const readGeneralField = (generalSettings, ...fields) => {
  if (!generalSettings) return '';
  for (const field of fields) {
    const value = generalSettings[field] ?? generalSettings?.values?.[field];
    if (value !== null && value !== undefined && value !== '') {
      return value;
    }
  }
  return '';
};

function Settings() {
  const { formthemeStyle, bodyheader, setDashboardHeader, dashboardStyle, bodyStyle } =
    useContext(TableContext);
  const { settingsgeneralSetStyle, settingsapiSetStyle, settingsprefSetStyle } =
    useContext(TableContext);

  const [submitingEmail, setSubmitingEmail] = useState(false);
  const [submitingGeneral, setSubmitingGeneral] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loader, setLoader] = useState(false);
  const dispatch = useDispatch();

  const [returnDays, setReturnDays] = useState('');
  const [estimatedDelivery, setEstimatedDelivery] = useState('');
  const [vendorCommissionPercent, setVendorCommissionPercent] = useState('');
  const [artistCommissionPercent, setArtistCommissionPercent] = useState('');
  const [minimumWithdrawal, setMinimumWithdrawal] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [taxRate, setTaxrate] = useState('');
  const [platformFee, setPlatformFee] = useState('0');
  const [settingsData, setSettingsData] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const loadSettings = () => {
    setLoading(true);
    const data = {
      actionUrl: 'settings',
      actionMethod: 'get',
    };
    dispatch(Entry(data)).then((resp) => {
      const settingsList = Array.isArray(resp.payload) ? resp.payload : [];
      const generalSettings =
        settingsList.find((item) => item.type === 'general') || settingsList[0] || null;

      setSettingsData(generalSettings);
      reset(generalSettings?.values || {});

      setReturnDays(readGeneralField(generalSettings, 'returnDays'));
      setEstimatedDelivery(readGeneralField(generalSettings, 'estimatedDelivery'));
      setVendorCommissionPercent(
        readGeneralField(generalSettings, 'vendorCommissionPercent', 'vendorCommition')
      );
      setArtistCommissionPercent(
        readGeneralField(generalSettings, 'artistCommissionPercent', 'artistCommition')
      );
      setMinimumWithdrawal(
        readGeneralField(generalSettings, 'minimumWithdrawal', 'minimumWithdrawal')
      );
      setShippingCost(
        readGeneralField(generalSettings, 'shippingCost', 'shippingCost')
      );
      setTaxrate(
        readGeneralField(generalSettings, 'taxRate', 'taxRate')
      );
      setPlatformFee(readGeneralField(generalSettings, 'platformFee') || '0');
      setLoading(false);
    });
  };

  const imageupload = (files) => {
    setLoader(true);
    const headers = {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'multipart/form-data',
    };
    const formData = new FormData();
    formData.append('File', files);
    axios
      .post(`${envValues.REACT_APP_API_ENDPOINT}/settings/logo`, formData, { headers })
      .then((response) => {
        setLoader(false);
        if (response.status === 200) {
          toast.success('Logo Updated Successfully');
          setSettingsData(response.data.data.settings);
          setCookies('SITE_SETTINGS', JSON.stringify(JSON.stringify(response.data.data.settings)));
        }
        return response.data;
      })
      .catch((e) => e);
  };

  const removePic = () => {
    setLoader(true);
    const headers = {
      'Access-Control-Allow-Origin': '*',
      'Content-Type': 'multipart/form-data',
    };
    const body = {
      logo: 'https://boilerplate-armia.s3.amazonaws.com/downloads/r512ndohjf0.png-1653567387848',
    };
    axios
      .post(`${envValues.REACT_APP_API_ENDPOINT}/settings/logoremove`, body, { headers })
      .then((response) => {
        setLoader(false);
        if (response.status === 200) {
          setSettingsData(response.data.data.settings);
          setCookies('SITE_SETTINGS', JSON.stringify(JSON.stringify(response.data.data.settings)));
          toast.success('Logo Updated Successfully');
        }
        return response.data;
      })
      .catch((e) => e);
  };

  const onSubmitAdminEmail = (formData) => {
    setSubmitingEmail(true);
    const requestData = {
      actionUrl: 'settings/general',
      actionMethod: 'patch',
      apiData: {
        values: {
          adminEmail: formData.adminEmail,
        },
      },
    };
    dispatch(Entry(requestData)).then((resp) => {
      setSubmitingEmail(false);
      if (resp.payload) {
        toast.success('Admin email updated successfully');
        loadSettings();
      }
    });
  };

  const handleSaveGeneralSettings = () => {
    setSubmitingGeneral(true);
    const requestData = {
      actionUrl: 'settings/updatedate',
      actionMethod: 'patch',
      apiData: {
        values: {
          returnDays,
          estimatedDelivery,
          vendorCommissionPercent,
          artistCommissionPercent,
          minimumWithdrawal,
          shippingCost: 0,
          taxRate: 0,
          platformFee,
        },
      },
    };
    dispatch(Entry(requestData)).then((resp) => {
      setSubmitingGeneral(false);
      if (resp.payload) {
        toast.success('General settings updated successfully.');
        loadSettings();
      }
    });
  };

  const imageHandler = (e) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        const files = e.target.files[0];
        imageupload(files);
      }
    };
    reader.readAsDataURL(e.target.files[0]);
  };

  useEffect(() => {
    loadSettings();
    setDashboardHeader('Settings');
    settingsgeneralSetStyle('settingsgeneral');
    settingsapiSetStyle('settingslinks');
    settingsprefSetStyle('settingslinks');
  }, []);

  const showPageLoader = loading || loader;

  return (
    <div className={styles[bodyStyle]}>
      <div
        className={styles[dashboardStyle]}
        style={{ minHeight: '100vh' }}
        id={styles.dashboardcont2}
      >
        {showPageLoader ? (
          <div style={{ textAlign: 'center', marginTop: '160px', color: '#7a68c2' }}>
            <SpinnerDotted style={{ color: '#39979d' }} />
          </div>
        ) : (
          <div className={styles[formthemeStyle]} id={styles.addform}>
            {settingsData?.logo ? (
              <>
                {/* <Image src={settingsData.logo} className={styles.logo} alt="Site logo" />
                <Popup
                  trigger={
                    <div className={styles.editbtnsection}>
                      <FontAwesomeIcon
                        icon={faPenToSquare}
                        className={styles[bodyheader]}
                        id={styles.penicon}
                      />
                    </div>
                  }
                  position="bottom left"
                >
                  <div className={styles.upload}>
                    <Label htmlFor="settings-logo-input">
                      <FontAwesomeIcon
                        icon={faArrowUpFromBracket}
                        className={styles.uploadicon}
                      />
                    </Label>
                    <input
                      id="settings-logo-input"
                      type="file"
                      name="image upload"
                      style={{ display: 'none' }}
                      accept="image/*"
                      placeholder="Update New Pic"
                      onChange={imageHandler}
                      aria-label="Upload site logo"
                    />
                    <FontAwesomeIcon
                      onClick={removePic}
                      icon={faUserXmark}
                      className={styles.crossicon}
                      role="button"
                      tabIndex={0}
                      aria-label="Remove site logo"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          removePic();
                        }
                      }}
                    />
                  </div>
                </Popup> */}
              </>
            ) : null}

            {/* <form onSubmit={handleSubmit(onSubmitAdminEmail)}>
              <Row>
                <Col md={4} sm={4}>
                  <Suspense
                    fallback={
                      <div>
                        <SpinnerDotted style={{ color: '#39979d' }} />
                      </div>
                    }
                  >
                    <EmailInput
                      className={styles.inputbox}
                      classNamedark={styles.inputbox1}
                      label="Admin Email"
                      labelstyle={styles.formlabel}
                      fieldName="adminEmail"
                      placeHolder="Enter Email Address"
                      register={register}
                      errors={errors}
                      isRequired
                    />
                  </Suspense>
                </Col>
              </Row>
              <div className={styles.savebtnsection}>
                <input
                  className={styles.savebtn}
                  type="submit"
                  disabled={submitingEmail}
                  value={submitingEmail ? 'Please wait..' : 'Save Admin Email'}
                />
              </div>
            </form> */}

            <hr className={styles.sectionDivider} />

            <h5 className={styles[bodyheader]} id={styles.addheading}>
              Return Days
            </h5>
            <input
              type="text"
              className={styles.selectdate}
              value={returnDays}
              onChange={(e) => setReturnDays(e.target.value)}
              placeholder="e.g. 10"
              aria-label="Return days"
            />

            <h5 className={`${styles[bodyheader]} ${styles.fieldheading}`} id={styles.addheading}>
              Estimated Delivery
            </h5>
            <input
              type="text"
              className={styles.selectdate}
              value={estimatedDelivery}
              onChange={(e) => setEstimatedDelivery(e.target.value)}
              placeholder="e.g. 3-5"
              aria-label="Estimated delivery"
            />

            <h5 className={`${styles[bodyheader]} ${styles.fieldheading}`} id={styles.addheading}>
              Vendor Commission (%)
            </h5>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              className={styles.selectdate}
              value={vendorCommissionPercent}
              onChange={(e) => setVendorCommissionPercent(e.target.value)}
              placeholder="e.g. 86"
              aria-label="Vendor commission percentage"
              disabled
            />

            <h5 className={`${styles[bodyheader]} ${styles.fieldheading}`} id={styles.addheading}>
              Artist Commission (%)
            </h5>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              className={styles.selectdate}
              value={artistCommissionPercent}
              onChange={(e) => setArtistCommissionPercent(e.target.value)}
              placeholder="e.g. 7"
              aria-label="Artist commission percentage"
              disabled
            />


            <h5 className={`${styles[bodyheader]} ${styles.fieldheading}`} id={styles.addheading}>
              Minimum Withdrawal Amount ($)
            </h5>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              className={styles.selectdate}
              value={minimumWithdrawal}
              onChange={(e) => setMinimumWithdrawal(e.target.value)}
              placeholder="e.g. 7"
              aria-label="Artist commission percentage"
            />



            <h5 className={`${styles[bodyheader]} ${styles.fieldheading}`} id={styles.addheading}>
              Platform Fee 
            </h5>
            <input
              type="number"
              min="0"
              step="0.01"
              className={styles.selectdate}
              value={platformFee}
              onChange={(e) => setPlatformFee(e.target.value)}
              placeholder="e.g. 5"
              aria-label="Platform fee percentage"
            />

            <div className={styles.savebtnsection}>
              <input
                className={styles.savebtn}
                type="button"
                disabled={submitingGeneral}
                value={submitingGeneral ? 'Please wait..' : 'Save General Settings'}
                onClick={handleSaveGeneralSettings}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Settings;


//updated 